import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useSetDataParamsByTicker } from "../lib/hooks/useSetDataParamsByTicker/useSetDataParamsByTicker";
import peadApi from "../lib/services/api";
import "../ui/Pead.css";
import type { Pead } from "epsm-api-types/models";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/Spinner";
import { useTabs } from "@/context/tabs/TabsContext";
import type { WidgetState } from "@/context/tabs/types";
import {
	DEFAULT_STATE_VALUES,
	DEFAULT_TICKER,
} from "@/shared/constants/common";
import type { ViewId } from "@/shared/types/common";
import { initialPeadValues } from "../consts";
import { getAccuracyForCurrentPead } from "../lib/helpers/getAccuracyForCurrentPead";
import { useGetWidgetWidth } from "../lib/hooks/useGetWidgetWidth/useGetWidgetWidth";
import { usePreparePriceForChart } from "../lib/hooks/usePreparePriceForChart";
import { UseData } from "../lib/services/UseData";
import type { PEADState, PeadFilterType } from "../types";
import { PeadSmallWidget } from "./components/PeadSmallWidget";

interface Props {
	widgetId: string;
	state: PEADState;
}

export const setPeadStateWithWidgetId = (
	widgetId: ViewId,
	state: PEADState,
	setWidgetState: (widgetId: ViewId, data: Partial<WidgetState>) => void,
	relativeToSpy: boolean,
) => {
	setWidgetState(widgetId as ViewId, {
		...state,
		relativeToSpy: !relativeToSpy,
	});
};

export const PeadWidget: React.FC<Props> = ({ widgetId, state }) => {
	const { setWidgetState } = useTabs();
	const ticker = state?.ticker || DEFAULT_STATE_VALUES.ticker || DEFAULT_TICKER;

	const { dataParams } = useSetDataParamsByTicker(ticker);
	const [activeChart, setActiveChart] = useState<Pead | null>(null);
	const [currentDrift, setCurrentDrift] = useState<Pead | null>(null);
	const showSeasonal = state?.peadFilter === "Seasonal";
	const relativeToSpy = state?.relativeToSpy || false;

	const peadFilter =
		state.peadFilter ?? (initialPeadValues.peadFilter as PeadFilterType);
	const historyType = state.historyType ?? initialPeadValues.historyType;
	const qtr = state.qtr ?? initialPeadValues.qtr;
	const stdDev = state.stdDev ?? initialPeadValues.stdDev;

	const { execute } = UseData.create().useGetData();
	const options = peadApi.getQueryOptions<Pead>(
		widgetId,
		ticker,
		dataParams,
		execute,
	);

	const { data = [], isPlaceholderData, isLoading } = useQuery(options);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		if (!qtr) return;

		const currentDrift =
			data.find((d: Pead) => {
				return qtr?.startsWith("20")
					? d.overall_count <= 20
					: showSeasonal
						? d.drift_unit.name === "seasonal" && d.overall_count > 20
						: d.drift_unit.name === "type_based" && d.overall_count > 20;
			}) || data[0];

		setActiveChart(currentDrift);
		setCurrentDrift(currentDrift);
	}, [data, qtr, historyType, showSeasonal]);

	const currentPeadAccy = getAccuracyForCurrentPead(data, activeChart);
	const { priceLine } = usePreparePriceForChart(activeChart, widgetId, ticker);

	const peadWidgetRef = useRef<HTMLDivElement>(null);
	const widgetWidth = useGetWidgetWidth(peadWidgetRef);

	if (isPlaceholderData || isLoading) {
		return <Spinner />;
	}

	if ((!data.length || !currentDrift || !activeChart) && !isLoading) {
		return (
			<div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
				{`PEAD not available for ${ticker}`}
			</div>
		);
	}

	if (peadFilter === "Seasonal" && qtr === "20 Qtr") {
		return (
			<div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
				{"Seasonal PEAD not available for 20 Qtr"}
			</div>
		);
	}

	return (
		<div
			className="pead grid grid-cols-2 max-w-full p-1  gap-1 h-full"
			id={`pead-${widgetId}`}
			ref={peadWidgetRef}
			data-testid="pead-widget"
		>
			<div className="col-span-full pead-content">
				<PeadSmallWidget
					relativeToSpy={relativeToSpy}
					data={data}
					widgetWidth={widgetWidth}
					activeChart={activeChart}
					ticker={ticker}
					stdDev={stdDev}
					priceLine={priceLine}
					currentPeadAccy={currentPeadAccy}
					historyType={historyType}
					peadFilter={peadFilter}
					showSeasonal={showSeasonal}
					currentDrift={currentDrift}
					qtr={qtr}
					widgetId={widgetId}
					setActiveChart={setActiveChart}
					setPeadStateWithWidgetId={() =>
						setPeadStateWithWidgetId(
							widgetId as ViewId,
							state,
							setWidgetState,
							relativeToSpy,
						)
					}
				/>
			</div>
		</div>
	);
};
